import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { LotSurveyPlan } from '../../../../types/lot-survey-plan';

interface EditLotSurveyPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plan: LotSurveyPlan;
}

export default function EditLotSurveyPlanModal({ isOpen, onClose, onSuccess, plan }: EditLotSurveyPlanModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    id: plan.surv_id || '',
    lot: plan.surv_lot || '',
    doc: plan.surv_doc || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Update the survey plan record
      const { error: updateError } = await supabase
        .from('lot_survey_plans')
        .update({
          surv_id: formData.id,
          surv_lot: formData.lot || null,
          surv_doc: formData.doc || null
        })
        .match({
          surv_id: plan.surv_id
        });

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating survey plan record:', err);
      setError(err instanceof Error ? err.message : 'Failed to update survey plan record');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium">Edit Survey Plan</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Survey ID *
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              required
            />
            <p className="mt-1 text-xs text-gray-500">Unique identifier for the survey plan</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lot
            </label>
            <input
              type="text"
              value={formData.lot}
              onChange={(e) => setFormData(prev => ({ ...prev, lot: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              placeholder="Enter lot identifier"
            />
            <p className="mt-1 text-xs text-gray-500">Lot reference or identifier</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Document
            </label>
            <input
              type="text"
              value={formData.doc}
              onChange={(e) => setFormData(prev => ({ ...prev, doc: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              placeholder="Enter document reference"
            />
            <p className="mt-1 text-xs text-gray-500">Document reference or file name</p>
          </div>

          <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
